<template>
  <div class="custom-data-table-wrapper">
    <div class="table-responsive">
      <table class="table table-striped table-hover table-sm mb-0">
        <thead>
          <tr>
            <th
              v-for="col in columns"
              :key="col.field || col.key"
              :width="col.width || 'auto'"
              scope="col"
              :class="[
                col.sortable !== false ? 'tablesorter-header cursor-pointer' : '',
                orderClass(col.field || col.key),
                col.headerClass || '',
                getHeaderAlignClass(col)
              ]"
              @click="col.sortable !== false && onSort(col.field || col.key)"
            >
              <slot :name="'header-' + (col.field || col.key)" :col="col">
                {{ col.label }}
              </slot>
            </th>
            <th
              v-if="hasActions"
              scope="col"
              :width="actionsWidth"
              :class="actionsHeaderClass || 'text-left'"
            >
              {{ actionsLabel }}
            </th>
          </tr>
        </thead>
        <tbody v-if="data && data.length">
          <slot name="body" :items="data">
            <tr
              v-for="(item, index) in data"
              :key="getItemKey(item, index)"
              :class="typeof rowClass === 'function' ? rowClass(item, index) : ''"
            >
              <slot name="row" :item="item" :index="index">
                <td
                  v-for="col in columns"
                  :key="col.field || col.key"
                  :class="[
                    col.cellClass || '',
                    getColAlignClass(col),
                    (col.sortable !== false && getColAlign(col) === 'right') ? 'sortable-num-cell' : ''
                  ]"
                >
                  <slot
                    :name="'cell-' + (col.field || col.key)"
                    :item="item"
                    :value="getFieldValue(item, col.field || col.key)"
                    :index="index"
                    :col="col"
                  >
                    <!-- Renderizado tipo badge automático para activo/enabled o col.type == 'badge' -->
                    <status-badge
                      v-if="col.type === 'badge' || (col.type !== 'text' && (col.field === 'activo' || col.field === 'enabled'))"
                      :value="getFieldValue(item, col.field || col.key)"
                      :active-text="col.activeText || 'Activo'"
                      :inactive-text="col.inactiveText || 'Inactivo'"
                    />

                    <!-- Formateador personalizado de columna -->
                    <span v-else-if="typeof col.formatter === 'function'">
                      {{ col.formatter(getFieldValue(item, col.field || col.key), item, index) }}
                    </span>

                    <!-- Renderizado de texto normal -->
                    <span v-else>
                      {{ getFieldValue(item, col.field || col.key) }}
                    </span>
                  </slot>
                </td>

                <!-- Columna de Acciones -->
                <td v-if="hasActions" class="text-center">
                  <slot name="actions" :item="item" :index="index"></slot>
                </td>
              </slot>
            </tr>
          </slot>
        </tbody>
      </table>
    </div>

    <!-- Estado vacío integrado -->
    <empty-state
      v-if="showEmptyState && (!data || !data.length)"
      :show="true"
      :message="emptyMessage"
    />
  </div>
</template>

<script>
import StatusBadge from "./StatusBadge.vue";
import EmptyState from "./EmptyState.vue";

export default {
  name: "DataTable",

  components: {
    StatusBadge,
    EmptyState,
  },

  props: {
    /**
     * Definición de columnas:
     * [
     *   { label: 'ID', field: 'id', width: '80', sortable: true, align: 'center' },
     *   { label: 'Nombre', field: 'name', sortable: true, align: 'left' },
     *   { label: 'Estado', field: 'activo', type: 'badge', width: '110' },
     *   { label: 'Fecha', field: 'created_at', formatter: (val) => val ? val.substring(0,10) : '' }
     * ]
     */
    columns: {
      type: Array,
      required: true,
      default: () => [],
    },

    /**
     * Lista de registros a mostrar
     */
    data: {
      type: Array,
      default: () => [],
    },

    /**
     * Campo único para identificar la fila (para key)
     */
    idKey: {
      type: [String, Function],
      default: "id",
    },

    /**
     * Campo por el cual está ordenada actualmente la tabla
     */
    orderByField: {
      type: String,
      default: "id",
    },

    /**
     * Dirección del ordenamiento: 'ASC' | 'DESC' | boolean
     */
    orderByDirection: {
      type: [String, Boolean],
      default: "DESC",
    },

    /**
     * Indica si se incluye la columna de acciones al final
     */
    hasActions: {
      type: Boolean,
      default: true,
    },

    /**
     * Título de la columna de acciones
     */
    actionsLabel: {
      type: String,
      default: "",
    },

    /**
     * Ancho de la columna de acciones
     */
    actionsWidth: {
      type: [String, Number],
      default: "110",
    },

    /**
     * Clases para la cabecera de acciones
     */
    actionsHeaderClass: {
      type: String,
      default: "text-left",
    },

    /**
     * Función opcional para aplicar clase a cada <tr>
     */
    rowClass: {
      type: Function,
      default: null,
    },

    /**
     * Mostrar automáticamente el EmptyState si no hay datos
     */
    showEmptyState: {
      type: Boolean,
      default: false,
    },

    /**
     * Mensaje para EmptyState
     */
    emptyMessage: {
      type: String,
      default: "No se encontraron registros que coincidan con la búsqueda.",
    },
  },

  methods: {
    getFieldValue(item, field) {
      if (!item || !field) return "";
      // Soporte para propiedades anidadas como 'usuario.nombre'
      if (typeof field === "string" && field.includes(".")) {
        return field.split(".").reduce((acc, part) => (acc ? acc[part] : ""), item);
      }
      const val = item[field];
      return val !== undefined && val !== null ? val : "";
    },

    getItemKey(item, index) {
      if (typeof this.idKey === "function") {
        return this.idKey(item, index);
      }
      if (item && this.idKey && item[this.idKey] !== undefined) {
        return item[this.idKey];
      }
      if (item) {
        const fallbackKey =
          item.id_producto ||
          item.id_servicio ||
          item.id_operador ||
          item.id_usuario ||
          item.id_keyword ||
          item.id_integrador ||
          item.id_billing_code ||
          item.id_billing_sequence ||
          item.id_atributo ||
          item.id_tarea;
        if (fallbackKey !== undefined) {
          return fallbackKey;
        }
      }
      return index;
    },

    orderClass(field) {
      if (!field || this.orderByField !== field) {
        return "";
      }
      if (typeof this.orderByDirection === "boolean") {
        return this.orderByDirection ? "asc" : "desc";
      }
      return String(this.orderByDirection).toUpperCase() === "ASC" ? "asc" : "desc";
    },

    onSort(field) {
      this.$emit("sort", field);
    },

    getColAlign(col) {
      if (!col) return "left";

      if (col.align) {
        return col.align;
      }

      const field = String(col.field || col.key || "").toLowerCase();
      const label = String(col.label || "").toLowerCase();
      const type = String(col.type || "").toLowerCase();

      // 1. Estados, badges, fechas y columnas de ese estilo: centrados
      if (
        type === "badge" ||
        type === "status" ||
        type === "estado" ||
        type === "date" ||
        type === "datetime" ||
        type === "time" ||
        type === "boolean" ||
        type === "bool" ||
        type === "icon" ||
        field === "activo" ||
        field === "enabled" ||
        field === "estado" ||
        field === "status" ||
        field === "state" ||
        field === "is_active" ||
        field === "nivel" ||
        field === "level" ||
        field === "fecha" ||
        field === "date" ||
        field === "created_at" ||
        field === "updated_at" ||
        field === "inicio" ||
        field === "fin" ||
        field.startsWith("fecha_") ||
        field.endsWith("_at") ||
        label === "estado" ||
        label === "status" ||
        label === "nivel" ||
        label === "fecha" ||
        label === "inicio" ||
        label === "fin" ||
        label.includes("estado")
      ) {
        return "center";
      }

      // 2. Tipos numéricos explícitos: alineados a la derecha
      if (
        type === "number" ||
        type === "numeric" ||
        type === "currency" ||
        type === "decimal" ||
        type === "int" ||
        type === "float" ||
        type === "integer"
      ) {
        return "right";
      }

      // 3. Tipos de texto explícitos: alineados a la izquierda
      if (type === "text" || type === "string") {
        return "left";
      }

      // 4. Por nombre de campo conocido numérico
      if (
        field === "id" ||
        field.startsWith("id_") ||
        field.endsWith("_id") ||
        field.startsWith("precio") ||
        field.includes("price") ||
        field.includes("tax") ||
        field === "impuesto" ||
        field === "impuestos" ||
        field === "numero" ||
        field === "numero_corto" ||
        field === "secuencia" ||
        field === "sequence" ||
        field === "lifetime" ||
        field === "frecuency" ||
        field === "frequency" ||
        field === "total" ||
        field === "monto" ||
        field === "cantidad" ||
        field === "count" ||
        field === "porcentaje" ||
        field === "percentage"
      ) {
        return "right";
      }

      // 5. Inspección dinámica de los datos en la tabla
      if (this.data && Array.isArray(this.data) && this.data.length > 0) {
        for (let i = 0; i < Math.min(this.data.length, 10); i++) {
          const val = this.getFieldValue(this.data[i], col.field || col.key);
          if (val !== undefined && val !== null && val !== "") {
            if (this.isNumericValue(val)) {
              return "right";
            }
            return "left";
          }
        }
      }

      // 6. Todo lo demás por defecto es texto -> a la izquierda
      return "left";
    },

    getColAlignClass(col) {
      const align = this.getColAlign(col);
      return "text-" + align;
    },

    getHeaderAlignClass(col) {
      if (!col) return "text-left";
      if (col.headerAlign) {
        return "text-" + col.headerAlign;
      }
      const field = String(col.field || col.key || "").toLowerCase();
      const label = String(col.label || "").toLowerCase();
      const type = String(col.type || "").toLowerCase();

      // Cabecera Estado centrada
      if (
        type === "badge" ||
        type === "status" ||
        type === "estado" ||
        field === "activo" ||
        field === "enabled" ||
        field === "estado" ||
        field === "status" ||
        field === "state" ||
        field === "is_active" ||
        label === "estado" ||
        label === "status" ||
        label.includes("estado")
      ) {
        return "text-center";
      }

      return "text-left";
    },

    isNumericValue(val) {
      if (typeof val === "number") return true;
      if (typeof val !== "string") return false;
      const str = val.trim();
      if (!str) return false;
      // Descartar fechas como 2026-10-06 o cadenas con formato 1:1 o rutas
      if (str.includes("-") || str.includes(":") || str.includes("/")) return false;
      return !isNaN(Number(str));
    },
  },
};
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
  user-select: none;
}

.tablesorter-header.text-right {
  padding-right: 24px;
}

.sortable-num-cell.text-right {
  padding-right: 24px;
}

.tablesorter-header.text-center {
  padding-left: 22px;
  padding-right: 22px;
}
</style>
